import { useMemo, useRef, useState } from "react";
import { addDays, format, isSameDay, isToday, startOfDay } from "date-fns";
import { nb } from "date-fns/locale";
import { toast } from "sonner";
import {
  useDeleteTimeEntry,
  useTimeEntries,
  type TimeEntry,
} from "@/hooks/use-time-registration";
import { formatHours, parseTaskDescription } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SrIcon } from "@/components/ui/SrIcon";
import NewRegistrationPopup from "./NewRegistrationPopup";
import { RegistrationEditDialog } from "./RegistrationEditDialog";

interface DayGridViewProps {
  userId: string | null;
}

/** 06–24: dekker shoot-dager (wireframe D, Erik 24.08). */
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 24;
const HOURS = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR + 1 }, (_, i) => DAY_START_HOUR + i);
const PX_PER_MIN = 1;
const TOTAL_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;
const GRID_HEIGHT = TOTAL_MINUTES * PX_PER_MIN;

/** Map phase keyword (from task_description parsing) to a Tailwind color class set. */
function phaseColors(phase: string): { bg: string; border: string; text: string } {
  const key = phase.toLowerCase();
  if (key.startsWith("pre")) {
    return { bg: "bg-blue-100/80", border: "border-blue-500", text: "text-blue-900" };
  }
  if (key.startsWith("prod")) {
    return { bg: "bg-emerald-100/80", border: "border-emerald-500", text: "text-emerald-900" };
  }
  if (key.startsWith("post")) {
    return { bg: "bg-orange-100/80", border: "border-orange-500", text: "text-orange-900" };
  }
  if (key.startsWith("reise")) {
    return { bg: "bg-rose-100/80", border: "border-rose-500", text: "text-rose-900" };
  }
  return { bg: "bg-slate-100/80", border: "border-slate-400", text: "text-slate-900" };
}

/**
 * Pull project name from a task_description in the format
 * "Prosjektnavn - Fase - Oppgave". Returns null if parsing fails.
 */
function parseProjectName(taskDescription: string | null): string | null {
  if (!taskDescription) return null;
  const parts = taskDescription.split(" - ");
  if (parts.length < 2) return null;
  const first = parts[0].trim();
  return first.length > 0 ? first : null;
}

interface PositionedEntry {
  entry: TimeEntry;
  topPx: number;
  heightPx: number;
  clippedTop: boolean;
  clippedBottom: boolean;
}

/** Compute top/height for an entry; clip to visible 06–24 window if it extends outside. */
function positionEntry(entry: TimeEntry): PositionedEntry | null {
  const start = new Date(entry.start_date);
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = startMin + Math.round(entry.duration_seconds / 60);

  const gridStart = DAY_START_HOUR * 60;
  const gridEnd = DAY_END_HOUR * 60;

  // Entry fully outside grid window
  if (endMin <= gridStart || startMin >= gridEnd) return null;

  const visibleStart = Math.max(startMin, gridStart);
  const visibleEnd = Math.min(endMin, gridEnd);

  return {
    entry,
    topPx: (visibleStart - gridStart) * PX_PER_MIN,
    heightPx: Math.max(8, (visibleEnd - visibleStart) * PX_PER_MIN),
    clippedTop: startMin < gridStart,
    clippedBottom: endMin > gridEnd,
  };
}

export default function DayGridView({ userId }: DayGridViewProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));
  const [showNewPopup, setShowNewPopup] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const newAnchorRef = useRef<HTMLButtonElement>(null);

  const dateStr = format(selectedDate, "yyyy-MM-dd");
  // useTimeEntries bounds the query to the inclusive calendar-day range
  // [dateStr, dateStr] → the single selected day (the hook tiles the upper
  // bound to an exclusive `dateStr + 1` internally, A4-smoke-fiks). Passing the
  // same day for both ends therefore returns exactly this day's entries; the
  // isSameDay filter below is belt-and-braces against any edge rows.
  const { data: entries = [] } = useTimeEntries(userId, dateStr, dateStr);
  const { mutate: deleteEntry } = useDeleteTimeEntry();

  const editingEntry = editingEntryId
    ? entries.find((e) => e.id === editingEntryId) ?? null
    : null;

  const positioned = useMemo(() => {
    return entries
      .filter((e) => isSameDay(new Date(e.start_date), selectedDate))
      .map(positionEntry)
      .filter((p): p is PositionedEntry => p !== null)
      .sort((a, b) => a.topPx - b.topPx);
  }, [entries, selectedDate]);

  const totalHours = useMemo(
    () => entries.reduce((sum, e) => sum + e.duration_seconds / 3600, 0),
    [entries],
  );

  const handlePrev = () => setSelectedDate((d) => addDays(d, -1));
  const handleNext = () => setSelectedDate((d) => addDays(d, 1));
  const handleToday = () => setSelectedDate(startOfDay(new Date()));

  const dateLabel = format(selectedDate, "EEEE d. MMMM yyyy", { locale: nb }).replace(/^\w/, (c) =>
    c.toUpperCase(),
  );

  return (
    <div>
      {/* Toolbar: date navigation */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 px-4 py-2 border-b border-border/50">
        <div className="flex items-center justify-between xl:justify-start gap-1">
          <button
            onClick={handlePrev}
            className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center hover:bg-muted rounded-md transition-colors"
            aria-label="Forrige dag"
          >
            <SrIcon name="chevron-left" className="w-4 h-4 text-muted-foreground" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="text-xs text-muted-foreground flex-1 xl:flex-none xl:min-w-[220px] text-center hover:text-foreground transition-colors"
                aria-label="Velg dato"
              >
                {dateLabel}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(startOfDay(d))}
                weekStartsOn={1}
                locale={nb}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <button
            onClick={handleNext}
            className="w-9 h-9 xl:w-7 xl:h-7 flex items-center justify-center hover:bg-muted rounded-md transition-colors"
            aria-label="Neste dag"
          >
            <SrIcon name="chevron-right" className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex items-center gap-2 self-center xl:self-auto">
          <span className="text-[11px] text-muted-foreground tabular-nums">
            Totalt {formatHours(totalHours)}
          </span>
          {!isToday(selectedDate) && (
            <button
              onClick={handleToday}
              className="text-[11px] px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
            >
              I dag
            </button>
          )}
          <button
            ref={newAnchorRef}
            onClick={() => setShowNewPopup(true)}
            className="text-[11px] px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1"
          >
            <SrIcon name="plus" className="w-3 h-3" />
            Ny
          </button>
        </div>
      </div>

      {/* Mobile list (under md): vertikalt-stablet liste av entries.
          Bruker samme sortering som desktop-grid (positioned er sortert på topPx ascending). */}
      <div className="md:hidden p-4">
        <div className="divide-y border rounded-md bg-background">
          {positioned.map((p) => {
            const { entry } = p;
            const { task } = parseTaskDescription(entry.task_description);
            const projectName = parseProjectName(entry.task_description);
            const start = new Date(entry.start_date);
            const startTime = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
            const endDate = new Date(start.getTime() + entry.duration_seconds * 1000);
            const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;
            const entryHours = entry.duration_seconds / 3600;

            return (
              <div key={entry.id} className="p-3 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  {projectName && (
                    <p className="text-[10px] text-muted-foreground truncate uppercase tracking-wide">
                      {projectName}
                    </p>
                  )}
                  <p className="text-sm font-medium truncate">
                    {task || entry.task_description}
                  </p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {startTime}–{endTime} · {formatHours(entryHours)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingEntryId(entry.id)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:bg-muted rounded shrink-0"
                  aria-label="Rediger registrering"
                >
                  <SrIcon name="pencil" className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    deleteEntry(entry.id, {
                      onSuccess: () => toast.success("Timer slettet"),
                      onError: (err) => toast.error((err as Error).message),
                    })
                  }
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-muted rounded shrink-0"
                  aria-label="Slett registrering"
                >
                  <SrIcon name="trash2" className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          {positioned.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Ingen registreringer denne dagen
            </p>
          )}
        </div>
        {entries.length > positioned.length && (
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            {entries.length - positioned.length} registrering(er) utenfor 06:00–24:00 vises ikke
          </p>
        )}
      </div>

      {/* Desktop grid (md+) */}
      <div className="hidden md:block p-4">
        <div className="relative" style={{ height: GRID_HEIGHT }}>
          {/* Hour rows */}
          {HOURS.map((h, idx) => {
            const isLast = idx === HOURS.length - 1;
            return (
              <div
                key={h}
                className={cn(
                  "absolute left-0 right-0 border-t border-border/40",
                  isLast && "border-b",
                )}
                style={{
                  top: (h - DAY_START_HOUR) * 60 * PX_PER_MIN,
                  height: 60 * PX_PER_MIN,
                }}
              >
                <span className="absolute -top-2 left-0 text-[10px] text-muted-foreground tabular-nums bg-card pr-1">
                  {String(h).padStart(2, "0")}:00
                </span>
              </div>
            );
          })}

          {/* Read-only grid: empty slots are NOT clickable. Use the "Ny"-button
              in the toolbar to add new entries; blokken har egen blyant-knapp
              for å åpne full redigerings-dialog. */}

          {/* Entry blocks */}
          {positioned.map((p) => {
            const { entry, topPx, heightPx, clippedTop, clippedBottom } = p;
            const { phase, task } = parseTaskDescription(entry.task_description);
            const projectName = parseProjectName(entry.task_description);
            const colors = phaseColors(phase);
            const start = new Date(entry.start_date);
            const startTime = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
            const endDate = new Date(start.getTime() + entry.duration_seconds * 1000);
            const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;
            const entryHours = entry.duration_seconds / 3600;

            return (
              <div
                key={entry.id}
                className={cn(
                  "group absolute left-10 right-0 border-l-4 rounded-r-md shadow-sm overflow-hidden p-2 z-10",
                  colors.bg,
                  colors.border,
                  "hover:ring-1 hover:ring-foreground/30",
                )}
                style={{ top: topPx, height: heightPx }}
                title={`${startTime}–${endTime} · ${entry.task_description}`}
              >
                {clippedTop && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-foreground/20" aria-hidden />
                )}
                {clippedBottom && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-foreground/20" aria-hidden />
                )}

                <div className="flex items-start justify-between gap-1">
                  <div className="min-w-0 flex-1">
                    {projectName && (
                      <p className={cn(
                        "text-[10px] truncate uppercase tracking-wide opacity-80",
                        colors.text,
                      )}>
                        {projectName}
                      </p>
                    )}
                    <p className={cn("text-[11px] font-medium truncate", colors.text)}>
                      {task || entry.task_description}
                    </p>
                    <p className={cn("text-[10px] tabular-nums opacity-80", colors.text)}>
                      {startTime}–{endTime} · {formatHours(entryHours)}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingEntryId(entry.id);
                      }}
                      className="p-1 hover:bg-muted rounded"
                      aria-label="Rediger registrering"
                    >
                      <SrIcon name="pencil" className="w-3 h-3 text-muted-foreground" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteEntry(entry.id, {
                          onSuccess: () => toast.success("Timer slettet"),
                          onError: (err) => toast.error((err as Error).message),
                        });
                      }}
                      className="p-1 hover:bg-destructive/20 rounded opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 transition-opacity"
                      aria-label="Slett"
                    >
                      <SrIcon name="trash2" className="w-3 h-3 text-destructive" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {positioned.length === 0 && (
          <p className="text-[11px] text-muted-foreground text-center mt-3">
            Ingen timer registrert. Trykk "Ny" for å legge til.
          </p>
        )}

        {entries.length > positioned.length && (
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            {entries.length - positioned.length} registrering(er) utenfor 06:00–24:00 vises ikke i rutenettet
          </p>
        )}
      </div>

      {showNewPopup && (
        <NewRegistrationPopup
          onClose={() => setShowNewPopup(false)}
          anchorRef={newAnchorRef}
          initialDate={dateStr}
        />
      )}

      <RegistrationEditDialog
        entry={editingEntry}
        open={!!editingEntryId}
        onOpenChange={(open) => {
          if (!open) setEditingEntryId(null);
        }}
      />
    </div>
  );
}
